import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Download, TrendingUp, Receipt } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin daily/weekly/monthly settlement report for /care renewals.
 *
 * For each LCO (or Distributor acting as their own LCO) we show:
 *   collected − GST(18%) − LCO cost = payout owed
 */
export default function CareSettlements() {
  const [period, setPeriod] = useState('day');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = (
    (typeof localStorage !== 'undefined' && (localStorage.getItem('access_token') || localStorage.getItem('token')))
    || ''
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL(`${API}/api/admin/care/settlements`);
      if (startDate && endDate) {
        url.searchParams.set('start_date', startDate);
        url.searchParams.set('end_date', endDate);
      } else {
        url.searchParams.set('period', period);
      }
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Failed to load');
      setData(j);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [period, startDate, endDate, token]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data?.rows?.length) return toast.info('No data to export');
    const headers = ['LCO Code', 'LCO Name', 'Renewals', 'Customer Collected', 'GST', 'LCO Cost', 'Payout Owed'];
    const lines = [
      headers.join(','),
      ...data.rows.map(r => [
        r.customer_code || '',
        (r.lco_name || '').replace(/,/g, ' '),
        r.renewals,
        r.customer_collected.toFixed(2),
        r.gst.toFixed(2),
        r.lco_cost.toFixed(2),
        r.payout_owed.toFixed(2),
      ].join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `care-settlements-${period}-${data.start.slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
          <Receipt className="w-5 h-5 text-indigo-600" /> LCO Settlement Report
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Payout owed to each LCO from ANT TV Care Portal subscriber renewals.
          Formula: <span className="font-mono">Customer Paid − GST − LCO Cost</span>
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            {['day', 'week', 'month'].map(p => (
              <Button key={p} size="sm"
                variant={period === p && !startDate ? 'default' : 'outline'}
                onClick={() => { setPeriod(p); setStartDate(''); setEndDate(''); }}
                data-testid={`settlement-period-${p}`}>
                {p === 'day' ? 'Today' : p === 'week' ? 'This week' : 'This month'}
              </Button>
            ))}
            <div className="flex items-center gap-1 text-xs text-slate-500 ml-2">
              <span>Or custom:</span>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="h-8 w-40 text-xs" data-testid="settlement-start-date" />
              <span>→</span>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="h-8 w-40 text-xs" data-testid="settlement-end-date" />
            </div>
            <div className="grow" />
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="settlement-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" onClick={exportCsv} disabled={!data?.rows?.length}
              className="bg-indigo-600 hover:bg-indigo-700" data-testid="settlement-export-csv">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
          </div>
        </CardHeader>
      </Card>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatTile label="Renewals"          value={data.totals.renewals} plain />
          <StatTile label="Customer Collected" value={data.totals.customer_collected} money highlight="indigo" />
          <StatTile label="GST"               value={data.totals.gst}                 money highlight="rose"   sub={`${(data.gst_rate * 100).toFixed(0)}%`} />
          <StatTile label="LCO Cost"          value={data.totals.lco_cost}            money highlight="amber" />
          <StatTile label="Payout Owed"       value={data.totals.payout_owed}         money highlight="emerald" />
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-600" /> Per LCO breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-slate-500 flex items-center gap-2 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : !data?.rows?.length ? (
            <div className="p-8 text-center text-slate-500">No renewals in this window.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2">LCO</th>
                    <th className="text-right px-4 py-2">Renewals</th>
                    <th className="text-right px-4 py-2">Customer Paid</th>
                    <th className="text-right px-4 py-2">GST</th>
                    <th className="text-right px-4 py-2">LCO Cost</th>
                    <th className="text-right px-4 py-2 text-emerald-700">Payout Owed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.lco_id} className="border-t border-slate-100 hover:bg-slate-50/60"
                      data-testid={`settlement-row-${r.lco_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{r.lco_name || r.lco_id}</div>
                        {r.customer_code && <Badge className="mt-1 bg-slate-100 text-slate-600 border-0 text-[10px] font-mono">{r.customer_code}</Badge>}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">{r.renewals}</td>
                      <td className="px-4 py-3 text-right text-slate-800 font-medium">₹{r.customer_collected.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-rose-600">₹{r.gst.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-amber-700">₹{r.lco_cost.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-700">₹{r.payout_owed.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatTile({ label, value, money, plain, highlight, sub }) {
  const colors = {
    indigo:  'text-indigo-700',
    rose:    'text-rose-700',
    amber:   'text-amber-700',
    emerald: 'text-emerald-700',
  };
  return (
    <Card className="p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-lg font-semibold ${colors[highlight] || 'text-slate-800'} mt-0.5`}>
        {money ? `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` :
                 Number(value || 0).toLocaleString('en-IN')}
      </div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </Card>
  );
}
